import React, { useEffect, useState, useContext, useRef } from 'react';
import { useHistory } from 'react-router';
import { ArrowRightIcon } from '@assets/icons/arrowRightIcon';
import Button from '@components/Button';
import Upload from './components/Upload';
import Files from './components/Files';
import TemplateName from './components/TemplateName';
import Preview from './components/Preview';
import Tags from './components/Tags';
import TemplateOrganization from '../ListTemplate/components/TemplateOrganization';
import BackgroundContainer from './components/BackgroundContainer';
import {IProps, IState, IIncompatibleFontReplace} from './types';
import WarningScreen from '@components/WarningScreen';
import IconButton from '@components/IconButton';
import Tooltip from '@components/Tooltip';
import actions from '@state/actions';
import selectors from '@state/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { openNewTemplateAction, reloadListAction } from '@state/openReducers/actions';
import * as S from './styles';
import D from '@assets/themes/dimensions';
import C from '@assets/themes/colors';
import { AuthContext } from '@utils/DOIgnition/AuthContext';
import { storageAccountName, containerName } from '@storage/azure-storage-blob';
import 'ag-psd/initialize-canvas';
import { Layer, Psd, readPsd, VectorContent } from 'ag-psd';
import { sendFile } from '@utils/cloudStorage';
import { cleanCanvas, createPreviews, getFontProps, addColorToPalette, IEffectColor } from '@utils/psd';
import { updateArtboardsZIndexChain } from '@utils/zIndexChains';
import { getAliases, getAutomaticSelectDescribe } from '@utils/selectDescribe';
import { cloneDeep } from 'lodash';
import FontReplacement from './components/FontReplacement';
import { IArtboardZIndexChain } from '@routes/private/Editor/types';
import { getBrandIdByOEMName } from 'utils/editor';
import { IDSConfig } from '@state/dsconfig/types';
import { ITemplate, IFileTemplate } from '@state/templates/types';
import { DEFAULT_DEALER_ORGANIZATION } from '@utils/DOIgnition/utils';
import { IUserType } from '@state/auth/types';
const uuid = require('react-uuid');

const NewTemplate:React.FC<IProps> = (props) => {
    const canvas = useRef<HTMLCanvasElement>(null);
    const dsConfig: IDSConfig = useSelector(selectors.dsConfig.getDSConfig);
    const storageToken = useSelector(selectors.storage.getToken);
    const authContext = useContext(AuthContext);
    const dispatch = useDispatch();
    const history: any = useHistory()
    const [filesResponse, setFileResponse] = useState<IState['response']>();
    const [expanded, setExpanded] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [files, setFiles] = useState<IFileTemplate[]>([]);
    const [open, setOpen] = useState(false);
    const [filesIncompatibleFonts, setFilesIncompatibleFonts] = useState<IIncompatibleFontReplace[]>([]);
    const [openFontsReplace, setOpenFontsReplace] = useState(false);
    const [warningOptions, setWarningOptions] = useState({
      title: '',
      subtitle: '',
      actionTitle: '',
      actionFunction: () => {},
      closeActionTitle: '',
      errorNote: '',
      errorArguments: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isAllNamesValid, setIsAllNamesValid] = useState([true]);
    const [invalidNames, setInvalidNames] = useState([]);
    const [hasWarnings, setHasWarnings] = useState(false);
    const [filesProgress, setFilesProgress] = useState<IState['filesProgress']>([]);

    const getTemplateByName = (name: string) => {
      let query=`?filter=c.name=%27${encodeURIComponent(name)}%27 and c.templateStatus!=%27INACTIVE%27&orderby=c.name&start=0&pagelength=1`;
      return new Promise((resolve, reject) => {
        dispatch(actions.templates.getTemplate(query, (result: any, error: any) => {
          if (error) {
            reject(error);
          } else {
            resolve(result);
          }
        }))
      })
    } 

    const handleWarningScreen = (openValue:boolean, title:string, subtitle:string, actionTitle:string, actionFunction:any, closeActionTitle:string, errorNote:string, errorArguments: any) => {
      setOpen(openValue);
      setWarningOptions({
        title: title,
        subtitle: subtitle,
        actionTitle: actionTitle,
        actionFunction: actionFunction,
        closeActionTitle: closeActionTitle,
        errorNote: errorNote,
        errorArguments: errorArguments,
      })
    }

    const handleExpand = () => {
      setExpanded(!expanded);
    }

    const handleCloseFunction = () => {
      dispatch(reloadListAction(false));
      dispatch(openNewTemplateAction(false));
      history.push({});
    }

    const handleClose = () => {
      if (files.length > 0) {
        handleWarningScreen(
          true, "Unsaved Progress",
          `Please don't refresh or close this screen until after you save your templates to avoid lost progress.\n\nYou can collapse your upload panel by clicking the blue arrow in the top left corner to keep working while files upload.`,
          "COLLAPSE PANEL", () => {handleCloseFunction()}, "CANCEL PROGRESS", "", []
        );
      } else {
        handleCloseFunction();
      }
    }

    const fetchDSConfig = () => {
      return new Promise((resolve, reject) => {
          const dealerUserData = {
            email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
            name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User",
            role: authContext?.userData.profile.role ? authContext?.userData.profile.role : "",
            organizations: authContext?.userData.profile.name ? authContext.userData.profile["dealeron.dealerorganization"] : []
          };  
            dispatch(actions.dsConfig.fetchDSConfig(dealerUserData, (result, error) => {
          if (error) reject(error)
          else resolve(result)
        }))
      });
    }

    const sendTemplate = (template: any) => {
      return new Promise((resolve, reject) => {
          dispatch(actions.templates.addTemplate(template, (result: any, error: any) => {
              if (error) {
                reject(error);
              } else {
                resolve(result);
              }
          }))
      })
    }

    const sendParsedTree = (parsedTree: any) => {      
      return new Promise((resolve, reject) => {
          dispatch(actions.parsedTrees.addParsedTree(parsedTree, (result: any, error: any) => {
              if (error) {
                reject(error);
              } else {
                resolve(result);
              }
          }))
      })
    }

    const readFileAsync = (file: File) => {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = () => {
          resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      })
    }

    const sendFileResponse = async (name: string, data: string) => {
      try {
        await sendFile(name, data, dispatch, undefined, undefined, storageToken);
        setFileResponse({name: name, status: 200});
      } catch(e) {
        setFileResponse({name: name, status: 400});
        console.error(e);
      }
    }

    const saveTemplates = () => {
      setIsAllNamesValid([true]);
      setInvalidNames([]);
      var isAllValid = true;
      files.map((file: any, index: number) => {
        if (!validateTemplate(file, index, true)) {
          isAllValid = false;
        }
      })
      if (isAllValid) {
        setIsLoading(true);
        validateTemplateNames();
      }
    }

    const validateTemplateNames = async () => {
      var newFiles: any = files;
      var newInvalidNames: any = [];
      var newIsAllNamesValid: boolean[] = [true];
      for (var i = 0; i < newFiles.length; i++) {
        var name = newFiles[i].file?.name === '' ? `File number ${i + 1}` : newFiles[i].file?.name;
        const repeatedTemplate :any = await getTemplateByName(newFiles[i].file.name);
        if (
          repeatedTemplate && repeatedTemplate.length > 0
        ) {
          newInvalidNames = [...newInvalidNames, name];
          setInvalidNames(newInvalidNames);
          newIsAllNamesValid = [...newIsAllNamesValid, false];
          setIsAllNamesValid([...newIsAllNamesValid]);
        } else {
          newIsAllNamesValid = [...newIsAllNamesValid, true];
          setIsAllNamesValid([...newIsAllNamesValid]);
        }
      }
    }

    useEffect(() => {
      if (isAllNamesValid && files.length > 0 && isAllNamesValid.length === (files.length + 1)) {
        finishSaveTemplates();
      }
    }, [isAllNamesValid])

    const finishSaveTemplates = async () => {
      const isValidIndex = isAllNamesValid.findIndex((item: boolean) => (item === false));
      if (isValidIndex < 0) {
        await Promise.all(files.map(async file => {
          const userData = {
            email: authContext?.userData.profile.email ? authContext?.userData.profile.email : "defaultuser@tangocode.com",
            name: authContext?.userData.profile.name ? authContext?.userData.profile.name : "Default User"
          };
          const templateId = uuid();
          const oemName = (file.tags?.oem ? file.tags.oem : "");
          const brandId: string = getBrandIdByOEMName(oemName, dsConfig);
          const described = Boolean(file.selectDescribe && ((file.selectDescribe.taggedFields && file.selectDescribe.taggedFields.length>0) || (file.selectDescribe.templateFields && file.selectDescribe.templateFields.length>0)));
          var newFile: Partial<ITemplate> = {
            id: templateId,
            createdBy: userData,
            updatedBy: userData,
            entityType: "TEMPLATE",
            name: file.file.name.trim(),
            renamed: file.renamed ? file.renamed : false,
            previews: [],
            parsedTree: file.id,
            tags: {
              designType: file.tags?.designType ? file.tags.designType : "", 
              platform: file.tags?.platform ? file.tags.platform : "",
              formats: file.tags?.formats ? file.tags.formats : [],
              oem: brandId,
              offerType: file.tags?.offerType ? file.tags.offerType : "",
              holiday: file.tags?.holiday ? file.tags.holiday : "",
              customTags: file.tags?.customTags ? file.tags.customTags : [],
            },
            described: described,
            organization: authContext?.userData?.profile.role === IUserType.DEALER_USER ? file.organization ?? DEFAULT_DEALER_ORGANIZATION : null,
          }

          const compressedData = cleanCanvas(file.data, filesIncompatibleFonts);
          const genPreviews = dsConfig.editorConfig.previewConfig.quantityGenerate??99;
          var newPreviews: any = [];
          file.previews?.forEach((preview: any, index:number) => {
            
            if (index< genPreviews)
            {                          
              let fileName: string = preview.name; 
              let artboard = compressedData.children?.find((artboard: any) => artboard.name===fileName)
              if (artboard && artboard.id) fileName = artboard.id;

              const previewFormat = dsConfig && dsConfig.editorConfig && dsConfig.editorConfig.previewConfig?dsConfig.editorConfig.previewConfig.format:"jpeg";
              const previewName = `${templateId}/${fileName}.${previewFormat}`;
              sendFileResponse(previewName, preview.url);
              const url = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${previewName}`;
              newPreviews = [...newPreviews, {name: fileName, url: url, size: preview.name }];
            }
          });
          newFile.previews = newPreviews;

          const arboardsZIndexChainData = updateArtboardsZIndexChain(compressedData, [],[],undefined); 
          delete file.data?.canvas;
          var newParsedTree = {
            id: file.id,
            entityType: "PARSEDTREE",
            colorPalette: file.colorPalette,
            selectDescribe: file.selectDescribe,
            artboardsZIndexChain: arboardsZIndexChainData,
            data: [compressedData],
            //parsedtree should not save psd, it gets error for some template because large data and also slow down 
            //the saving when data change, if psd need it it should be saved in a new entity compressed
            //psd: file.data,
            createdBy: userData,
            updatedBy: userData
          }
          await sendTemplate(newFile);
          sendParsedTree(newParsedTree);
        }));
        history.push({pathname: '/templates'});
        dispatch(openNewTemplateAction(false));
        dispatch(reloadListAction(true));
        setIsLoading(false);
      } else {
        handleWarningScreen(
          true, "Repeated Template Name",
          `The template name is already taken in Design Studio.`,
          "", undefined, "OKAY", "No two templates can share same name", [...invalidNames]
        );
        setIsLoading(false);
      }
    }

    const parseToDSElements = (newDSConfig: IDSConfig, object: Layer & Psd, initLength: number, initColorPalette: string[], initArtboardsZIndexChain: IArtboardZIndexChain[], initIncompatibleFonts: string[], parentId: string) => {     

      let newObject: any = {...object};
      let newIncompatibleFonts: string[] = [...initIncompatibleFonts];
      let objectsLength = initLength;
      let objectsColorPalette = [...initColorPalette];
      let objectsArtboardsZIndexChain = [...initArtboardsZIndexChain];
      if (object?.children?.length) {
        newObject.children = [];
        object.children.map(child => {
          const canvasId = uuid();
          let canvasData = "";
          const canvasMaskId = uuid();
          let canvasMaskData = "";
          if (child.canvas) {
              objectsLength += 1;
              canvasData = child.canvas.toDataURL();
              sendFileResponse(`${parentId}/${canvasId}.png`, canvasData);
              
          }
          if (child.mask?.canvas) {
              objectsLength += 1;
              canvasMaskData = child.mask.canvas.toDataURL();
              sendFileResponse(`${parentId}/${canvasMaskId}.png`, canvasMaskData);
          }
          if (child.text) {
              let { fillColors, incompatibleChildFonts } = getFontProps(newDSConfig, child.text);
              objectsColorPalette = [...objectsColorPalette, ...fillColors];
              objectsColorPalette = objectsColorPalette.filter((item, index) => objectsColorPalette.indexOf(item) === index);
              newIncompatibleFonts = [...newIncompatibleFonts, ...incompatibleChildFonts];
              newIncompatibleFonts = newIncompatibleFonts.filter((item, index) => newIncompatibleFonts.indexOf(item) === index);
          }
          if (child?.vectorStroke?.content) {
              const content = child?.vectorStroke?.content as VectorContent & IEffectColor;
              if (content.color) objectsColorPalette = addColorToPalette(content.color, objectsColorPalette);
          }
          if (child?.vectorFill) {
              const vectorFill = child?.vectorFill as VectorContent & IEffectColor;
              if (vectorFill.color) objectsColorPalette = addColorToPalette(vectorFill.color, objectsColorPalette);
          }
          const {objects, length, colorPalette, artboardsZIndexChain, incompatibleFonts} = parseToDSElements(newDSConfig, child as Layer & Psd, objectsLength, objectsColorPalette, objectsArtboardsZIndexChain, newIncompatibleFonts, parentId);
          objectsLength = length;
          objectsColorPalette = colorPalette;
          objectsArtboardsZIndexChain = artboardsZIndexChain;
          newIncompatibleFonts = incompatibleFonts;
          const children = newObject.children ?? [];
          let newChild = {
            ...objects, 
            id: canvasId, 
            hidden: child.hidden ? child.hidden : false,
          }
          if (child.mask) newChild.mask = {...child.mask, id: canvasMaskId};
          if (newChild.canvas) newChild.image = true;
          delete newChild.canvas;
          delete newChild.mask?.canvas;
          newObject.children = [...children, newChild];
        });
      }
      newObject.id = uuid();
      delete newObject.linkedFiles;
      newObject.hidden = newObject.hidden ? newObject.hidden : false;
      return {
        objects: newObject, 
        length: objectsLength, 
        colorPalette: objectsColorPalette, 
        artboardsZIndexChain: objectsArtboardsZIndexChain,
        incompatibleFonts: newIncompatibleFonts,
      };
    }

    const validateTemplate = (file:any, index: number, openWarning: boolean) => {
      var isValid = true;
      var name = file.file?.name === '' ? `File number ${index + 1}` : file.file?.name;
      const reapeatedFiles: any = files.filter((fileItem: any) => (fileItem.file.name === file.file.name));
      const fileProgressIndex = filesProgress.findIndex((fileProgress: any) => (fileProgress.id === file.id));
      if (
        file.tags === undefined
        || file.tags.designType === undefined || file.tags.platform === undefined || file.tags.formats === undefined || file.tags.oem === undefined
      ) {
        isValid = false;
        if (openWarning)
        handleWarningScreen(
          true, "Missing Required Tags",
          `All templates need to be have the required tags`,
          "", undefined, "OKAY", "Missing required tags", [name]
        );
      }
      if (
        filesProgress[fileProgressIndex]?.progress < file.canvasLength
      ) {
        isValid = false;
        if (openWarning)
        handleWarningScreen(
          true, "Upload progress",
          `The upload process is not finished, or the system is not able to upload your file.`,
          "", undefined, "OKAY", "Upload process failure", [name]
        );
      }
      if (
        reapeatedFiles && reapeatedFiles.length > 1
      ) {
        isValid = false;
        if (openWarning)
        handleWarningScreen(
          true, "Repeated Template Name",
          `Each template needs a unique name`,
          "", undefined, "OKAY", "No two templates can share same name", [name]
        );
      }
      if (
        file.file?.name === undefined || file.file.name === ""
      ) {
        isValid = false;
        if (openWarning)
        handleWarningScreen(
          true, "Missing Template Name",
          `You must enter a name for all of your templates`,
          "", undefined, "OKAY", "Unnamed file", [name]
        );
      }
      return isValid;
    }

    useEffect(() => {
      const splitResponse = filesResponse?.name?.split("/");
      if (splitResponse && splitResponse.length > 1) {
        const fileIndex = filesProgress?.findIndex((fileProgress: any) => (fileProgress.id === splitResponse[0]));
        if (fileIndex >= 0) {
          if (filesResponse?.status === 400) {
            setFilesProgress([
              ...filesProgress.slice(0, fileIndex),
              {...filesProgress[fileIndex], hasErrors: true},
              ...filesProgress.slice(fileIndex + 1)
            ]);
            return;
          } else if (filesResponse?.status === 200) { 
            setFilesProgress([
              ...filesProgress.slice(0, fileIndex),
              {...filesProgress[fileIndex], progress: filesProgress[fileIndex].progress + 1},
              ...filesProgress.slice(fileIndex + 1)
            ]);
            const fileDataIndex = files?.findIndex((file: any) => file.id === splitResponse[0]);
            if (fileDataIndex >= 0 && filesProgress[fileIndex].progress + 1 === files[fileDataIndex].canvasLength) {
              setFiles([
                ...files.slice(0, fileDataIndex),
                {...files[fileDataIndex], file: {name: files[fileDataIndex].file.name}},
                ...files.slice(fileDataIndex + 1)
              ]);
            }
          }
        } else {
          setFilesProgress([...filesProgress, {id: splitResponse[0], progress: 1, hasErrors: false}]);
        }
      }
    }, [filesResponse])

    useEffect(() => {
      if (filesIncompatibleFonts.length>0) {
        setOpenFontsReplace(true);
      }
    }, [filesIncompatibleFonts.length])

    const addToIncompatibleFonts = (incompatibleFonts: string[]) => {
      if (incompatibleFonts && incompatibleFonts.length>0) {
        let actualFilesIncompatibleFonts = cloneDeep(filesIncompatibleFonts);
        for (let i=0;i<incompatibleFonts.length;i++) {
          if (!(actualFilesIncompatibleFonts.map((actualFilesIncompatibleFont) => { return actualFilesIncompatibleFont.incompatibleFont}).includes(incompatibleFonts[i]))) {
            actualFilesIncompatibleFonts.push({incompatibleFont: incompatibleFonts[i], fontReplace: undefined});
          }
        } 
        setFilesIncompatibleFonts(actualFilesIncompatibleFonts);
      }
    }

    useEffect(() => {
      if (files && files.length > 0) {
        const updateFiles = async () => {
          const newDSConfig = await fetchDSConfig() as IDSConfig;
          var isAllValid = true;
          var newFiles: IFileTemplate[] = [];
          var hasChanged: Boolean = false;
          var allNewIncompatibleFonts: string[] = [];
          for (var i = 0; i < files.length; i++) {
            const file = files[i];
            if (!validateTemplate(file, i, false)) {
              isAllValid = false;
            }
            if (file.data === undefined) {
              try {
                hasChanged = true;
                const buffer: any = await readFileAsync(file.file as File);
                let psdTree = undefined;
                try {
                  psdTree = readPsd(buffer);       
                } catch (error) {
                  throw error;
                }
                const {objects, length, colorPalette, artboardsZIndexChain, incompatibleFonts} = parseToDSElements(newDSConfig, psdTree, 0, [], [], [], file.id);                
                allNewIncompatibleFonts = allNewIncompatibleFonts.concat(incompatibleFonts);
                const previews = createPreviews(canvas.current, objects);
                const aliases = getAliases(dsConfig.selectDescribeConfig.categories);
                const selectDescribe = getAutomaticSelectDescribe(objects.children as any, aliases, dsConfig.selectDescribeConfig.categories);
                newFiles = [
                  ...newFiles,
                  {
                    ...file,
                    data: objects, 
                    artboardsZIndexChain: artboardsZIndexChain, 
                    previews: previews,
                    canvasLength: length, 
                    colorPalette: colorPalette,
                    selectDescribe: selectDescribe,
                  }
                ];
              } catch(error: any) {
                handleWarningScreen(
                  true, "File Upload Error",
                  `Your file is unsupported or corrupted and could not be uploaded.`,
                  "", undefined, "OKAY", error ? error.toString().replace('Error:', '') : "Upload process failure", [file.file.name]
                );
                console.error(error);
              }
            } else {
              newFiles = [...newFiles, file];
            }
          }
          addToIncompatibleFonts(allNewIncompatibleFonts);
          if (hasChanged === true) setFiles([...newFiles]);
          setHasWarnings(!isAllValid);
        }
        updateFiles();
      } else {
        setHasWarnings(true);
      }
      if (files && filesProgress?.length > 0) {
        let newProgressFiles = filesProgress.filter(( file:any )=> {
          let fileIndex = files.findIndex( (item:any) => item.id === file.id);
          if (fileIndex!==-1) return file;
        })
        setFilesProgress(newProgressFiles);
      }
    }, [files])

    const handleClickIgnore = () => {
      let actualFilesIncompatibleFonts = cloneDeep(filesIncompatibleFonts);
      for (let i=0;i<actualFilesIncompatibleFonts.length;i++) {
        actualFilesIncompatibleFonts[i].fontReplace = undefined;
      }
      setFilesIncompatibleFonts(actualFilesIncompatibleFonts);
      setOpenFontsReplace(false);
    }
    
    const handleClickReplace = () => {
      setOpenFontsReplace(false);
    }

    const handleFontSelect = (incompatibleFont: string, fontReplace: string|undefined) => {
      let actualFilesIncompatibleFonts = cloneDeep(filesIncompatibleFonts);
      for (let i=0;i<actualFilesIncompatibleFonts.length;i++) {
        if (actualFilesIncompatibleFonts[i].incompatibleFont === incompatibleFont) {
          actualFilesIncompatibleFonts[i].fontReplace = fontReplace;
        }
      }
      setFilesIncompatibleFonts(actualFilesIncompatibleFonts);
    }

    return (
      <>
        <WarningScreen
          open={open}
          setOpen={setOpen}
          options={warningOptions}
        />
        {
          openFontsReplace &&
          <FontReplacement 
            incompatibleFonts={filesIncompatibleFonts} 
            onClickIgnore={handleClickIgnore} 
            onClickReplace={handleClickReplace} 
            onFontSelect={handleFontSelect} 
          />
        }
        <S.PreviewsCanvas
          id={`previews-canvas`} 
          ref={canvas}
        />
        <S.Root 
          rightValue={expanded ? `0px` : 
            `calc(${D.BACKGROUND_UPLOAD_WIDHT + D.LOGOUT_WIDTH}px - 100vw)`
          } 
        >
            <S.BlurFrame 
              opacityValue={expanded ? 1 : 0}
              widthValue={expanded ? `${D.LOGOUT_WIDTH}px` : '0px'} 
            />
            <S.ArrowIconContainer 
              onClick={handleExpand}
              topValue={expanded ? `13px` : `51px`}
              leftValue={expanded ? `calc(${D.LOGOUT_WIDTH}px + 40px)` : `-20px`}
            >
              <Tooltip
                tip={expanded ? "Collapse" : "Expand"}
              >
                <IconButton>
                  <S.RotateIcon rotationValue={expanded ? `rotate(0deg)` : `rotate(180deg)`} >
                    <ArrowRightIcon fillColor="black"/>
                  </S.RotateIcon>
                </IconButton>
              </Tooltip>
            </S.ArrowIconContainer>
            {
              expanded ?
                <S.Container 
                  widthValue={`calc(100vw - ${D.LOGOUT_WIDTH}px)`} 
                >
                  <S.Header>
                    <S.Right>
                        <S.Title>New Template</S.Title>
                    </S.Right>
                    <S.Left>
                        <S.LeftText>If you exit out of this page before you are done you will lose your progress</S.LeftText>
                        <Button
                            label="CANCEL"
                            style={{marginLeft: 25, marginRight: 25, hoverBackgroundColor: "#fdefe7", backgroundColor: "transparent", border: "1px solid #ff7319", width: 112}}
                            labelStyle={{color: "#ff7319"}}
                            onClick={handleClose}
                        />
                        <Button 
                            label={isLoading ? "SAVING..." : "CREATE TEMPLATE"} 
                            style={{hoverBackgroundColor: `${C.DARK_ORANGE}88`, backgroundColor: C.DARK_ORANGE, width: 167}} 
                            onClick={!isLoading && files.length > 0 ? saveTemplates : () => {}}
                            disabled={hasWarnings}
                        />
                    </S.Left>
                  </S.Header>
                  <S.Body>
                    <S.Column style={{borderRight: '1px solid #dddddd'}}>
                        <Upload files={files} setFiles={setFiles} handleWarningScreen={handleWarningScreen} />
                        <Files files={files} setFiles={setFiles} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex} filesProgress={filesProgress} setFilesProgress={setFilesProgress} />
                    </S.Column>
                    <S.Column>
                        <TemplateName files={files} setFiles={setFiles} currentIndex={currentIndex} />
                        <Preview files={files} setFiles={setFiles} currentIndex={currentIndex} filesProgress={filesProgress}/>
                        {
                          (authContext?.userData?.profile.role === IUserType.DEALER_USER && files.length > 0) &&
                          <TemplateOrganization
                            files={files}
                            setFiles={setFiles}
                            currentIndex={currentIndex}
                          />
                        }
                        <Tags files={files} setFiles={setFiles} currentIndex={currentIndex} />
                    </S.Column>
                  </S.Body>
                </S.Container >
              :
                <S.Container 
                    widthValue={`calc(100vw - 134px)`} 
                  >
                  <BackgroundContainer 
                    files={files}
                    filesProgress={filesProgress}
                    isLoading={isLoading}
                  />
                </S.Container>
            }
        </S.Root >
      </>
    );
}

export default NewTemplate